import { colors, typography } from '../../../styles/theme';

/**
 * Tokens del alta de lista por plano CAD. Mismo chrome oscuro que el panel
 * Listas: bg/base, bg/elevated, borde y verde de marca desde `theme.js`.
 */

const BG_ELEVATED = colors.backgroundDark;
const TEXT_PRIMARY = colors.textPanelTitle;
const TEXT_SECONDARY = '#ababab';
const PRIMARY_ON = '#e8f4f4';

/** Va dentro del box del alta de lista, que ya aporta el padding vertical. */
export const flowRootSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minHeight: 0,
};

export const sectionLabelSx = {
  color: '#FFFFFF',
  fontFamily: typography.fontFamily.heading,
  fontWeight: typography.fontWeight.semibold,
  fontSize: '16px',
  lineHeight: '20px',
  mb: 1,
  display: 'block',
};

export const helperTextSx = {
  color: TEXT_SECONDARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  lineHeight: '18px',
};

/** Tarjeta de elección: lista común vs plano CAD. */
export const getChoiceCardSx = (selected, disabled = false) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  width: '100%',
  textAlign: 'left',
  textTransform: 'none',
  p: '14px',
  borderRadius: '8px',
  border: '1px solid',
  borderColor: selected ? colors.primary : colors.border,
  bgcolor: selected ? 'rgba(72,146,145,0.12)' : BG_ELEVATED,
  color: TEXT_PRIMARY,
  boxShadow: 'none',
  justifyContent: 'flex-start',
  cursor: disabled ? 'default' : 'pointer',
  '&:hover': disabled
    ? { boxShadow: 'none' }
    : {
      bgcolor: selected ? 'rgba(72,146,145,0.18)' : '#2a2a2a',
      borderColor: selected ? colors.primary : 'rgba(255,255,255,0.28)',
      boxShadow: 'none',
    },
  '&.Mui-disabled': {
    color: TEXT_PRIMARY,
    borderColor: colors.border,
    bgcolor: BG_ELEVATED,
    opacity: 0.45,
  },
});

export const choiceIconBoxSx = {
  width: 36,
  height: 36,
  borderRadius: '8px',
  bgcolor: 'rgba(255,255,255,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export const choiceIconSx = { fontSize: 20, color: PRIMARY_ON };

export const choiceTitleSx = {
  color: '#FFFFFF',
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.semibold,
  fontSize: '14px',
  lineHeight: '18px',
};

export const choiceDescriptionSx = {
  color: TEXT_SECONDARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '12.5px',
  lineHeight: '17px',
  mt: 0.25,
};

/** Alto del slot de drop / archivo: no se achica al elegir el .dxf. */
const PLAN_DROP_SLOT_MIN_HEIGHT = 132;

/** Recuadro único: drop del `.dxf` + requisitos. */
export const getPlanCardSx = (dragging, hasError, hasFile) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  p: '16px 16px 12px',
  borderRadius: '8px',
  border: hasFile ? '1px solid' : '1px dashed',
  borderColor: hasError
    ? colors.error
    : (dragging ? colors.primary : colors.border),
  bgcolor: dragging ? 'rgba(72,146,145,0.10)' : BG_ELEVATED,
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
});

/** Overlay de carga centrado sobre el recuadro del plano. */
export const planBusyOverlaySx = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.25,
  px: 2,
  borderRadius: '8px',
  bgcolor: colors.backgroundDark,
};

export const dropZoneInnerSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  minHeight: PLAN_DROP_SLOT_MIN_HEIGHT,
  px: 1,
  py: 2,
  boxSizing: 'border-box',
  cursor: 'pointer',
  textAlign: 'center',
};

export const dropIconSx = { fontSize: 30, color: TEXT_PRIMARY };

export const dropTitleSx = {
  color: TEXT_PRIMARY,
  fontFamily: typography.fontFamily.heading,
  fontWeight: typography.fontWeight.semibold,
  fontSize: '15px',
  lineHeight: '19px',
};

export const dropHintSx = {
  ...helperTextSx,
  maxWidth: 320,
};

export const requirementsBoxSx = {
  width: '100%',
  pt: 1.5,
  px: 0.25,
  borderTop: `1px solid ${colors.border}`,
  textAlign: 'left',
};

export const requirementItemSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  color: TEXT_SECONDARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '12.5px',
  lineHeight: '18px',
};

export const requirementIconSx = { fontSize: 15, color: colors.primary, mt: '1px', flexShrink: 0 };

/** Slot que conserva el alto del drop; el archivo va en un recuadro adentro. */
export const selectedFileSlotSx = {
  display: 'flex',
  alignItems: 'center',
  minHeight: PLAN_DROP_SLOT_MIN_HEIGHT,
  boxSizing: 'border-box',
};

export const selectedFileRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  width: '100%',
  p: '10px 12px',
  minHeight: 56,
  borderRadius: '8px',
  border: `1px solid ${colors.border}`,
  bgcolor: colors.backgroundSurface,
  boxSizing: 'border-box',
};

export const selectedFileNameSx = {
  color: '#FFFFFF',
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.medium,
  fontSize: '13px',
  lineHeight: '17px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const selectedFileMetaSx = {
  color: TEXT_SECONDARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '12px',
  lineHeight: '16px',
};

export const errorTextSx = {
  color: colors.error,
  fontFamily: typography.fontFamily.primary,
  fontSize: '12.5px',
  lineHeight: '17px',
};

export const warningBoxSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.25,
  p: '10px 12px',
  borderRadius: '8px',
  border: `1px solid ${colors.warning}`,
  bgcolor: 'rgba(237,108,2,0.10)',
};

export const warningTextSx = {
  color: '#F3D6B4',
  fontFamily: typography.fontFamily.primary,
  fontSize: '12.5px',
  lineHeight: '17px',
};

/** Resumen del informe del parse. */
export const summaryBoxSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  p: '12px',
  borderRadius: '8px',
  bgcolor: BG_ELEVATED,
  border: `1px solid ${colors.border}`,
};

export const summaryRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
};

export const summaryLabelSx = {
  color: TEXT_SECONDARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '12.5px',
  lineHeight: '17px',
};

export const summaryValueSx = {
  color: '#FFFFFF',
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.semibold,
  fontSize: '12.5px',
  lineHeight: '17px',
};

/** Miniatura del plano: mismo lienzo canvas que el detalle CAD. */
export const previewStageSx = {
  position: 'relative',
  width: '100%',
  height: 220,
  borderRadius: '8px',
  overflow: 'hidden',
  border: `1px solid ${colors.border}`,
  bgcolor: colors.backgroundDark,
};

/** Ícono en el vértice superior derecho del recuadro de preview. */
export const previewExpandButtonSx = {
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 2,
  width: 28,
  height: 28,
  p: 0,
  color: colors.textPanelTitle,
  bgcolor: colors.backgroundDark,
  border: `1px solid ${colors.border}`,
  boxShadow: 'none',
  '&:hover': {
    color: colors.textPanelTitle,
    bgcolor: '#2a2a2a',
    borderColor: 'rgba(255,255,255,0.28)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.35)',
    bgcolor: colors.backgroundDark,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 16,
  },
};

export const previewCaptionSx = {
  ...helperTextSx,
  mt: 0.75,
  display: 'block',
};

export const busyTextSx = {
  color: TEXT_PRIMARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13.5px',
  lineHeight: '18px',
  textAlign: 'center',
};

export const actionsRowSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
};

export const secondaryButtonSx = {
  textTransform: 'none',
  height: 44,
  minHeight: 44,
  minWidth: 97,
  px: '24px',
  borderRadius: '8px',
  fontFamily: typography.fontFamily.primary,
  fontSize: '16px',
  fontWeight: typography.fontWeight.medium,
  lineHeight: 1.2,
  color: '#FFFFFF',
  borderColor: 'rgba(255,255,255,0.85)',
  bgcolor: 'transparent',
  boxShadow: 'none',
  '&:hover': {
    borderColor: '#FFFFFF',
    bgcolor: 'rgba(255,255,255,0.06)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.45)',
    borderColor: 'rgba(255,255,255,0.28)',
  },
};

export const primaryButtonSx = {
  textTransform: 'none',
  height: 44,
  minHeight: 44,
  minWidth: 107,
  px: '24px',
  borderRadius: '8px',
  fontFamily: typography.fontFamily.primary,
  fontSize: '16px',
  fontWeight: typography.fontWeight.medium,
  lineHeight: 1.2,
  bgcolor: colors.primary,
  color: PRIMARY_ON,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: colors.primaryHover,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: '#3a3a3a',
    color: 'rgba(255,255,255,0.35)',
  },
};

export const textButtonSx = {
  textTransform: 'none',
  color: TEXT_SECONDARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  fontWeight: typography.fontWeight.medium,
  px: 1,
  '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
};

export const nameFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2e2e2e',
    borderRadius: '8px',
    minHeight: 40,
    height: 40,
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
  '& .MuiOutlinedInput-input': {
    color: '#FFFFFF',
    fontFamily: typography.fontFamily.primary,
    fontSize: '14px',
    lineHeight: '20px',
    py: '8px',
    px: '9px',
    '&::placeholder': { color: TEXT_SECONDARY, opacity: 1 },
  },
};
